/**
 * Equipment Data Viewer - Admin tool for reviewing merged equipment catalog data.
 * Hidden page (not in navigation) for validating merge quality.
 * Includes merge/unmerge functionality for manual corrections.
 */
import { useState, useMemo } from 'react';
import {
  Container,
  Title,
  Stack,
  Paper,
  Alert,
  Table,
  ScrollArea,
  Text,
  TextInput,
  Group,
  Loader,
  Center,
  Badge,
  Anchor,
  Pagination,
  Select,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import {
  IconAlertCircle,
  IconSearch,
  IconExternalLink,
  IconFilter,
  IconUnlink,
  IconGitMerge,
} from '@tabler/icons-react';
import { useEquipmentViewer, useEquipmentCatalogCategories, useEquipmentCatalogBrands } from '@/hooks/useEquipmentViewer';
import { useAuth } from '@/contexts/AuthContext';
import type { EquipmentViewerEntry } from '@/types/equipmentCatalog';
import { EquipmentUnmergePanel, EquipmentMergePanel } from '@/components/EquipmentMerge';

const PAGE_SIZE = 50;

function PriceCell({ usd, gbp }: { usd: number | null; gbp: number | null }) {
  if (!usd && !gbp) return <Text size="xs" c="dimmed">-</Text>;
  return (
    <Stack gap={0}>
      {usd && <Text size="xs">${usd.toFixed(2)}</Text>}
      {gbp && <Text size="xs">&pound;{gbp.toFixed(2)}</Text>}
    </Stack>
  );
}

function SourceLink({ url, label }: { url: string | null; label: string }) {
  if (!url) return <Text size="xs" c="dimmed">-</Text>;
  return (
    <Anchor href={url} target="_blank" rel="noopener noreferrer" size="xs">
      {label} <IconExternalLink size={12} style={{ verticalAlign: 'middle' }} />
    </Anchor>
  );
}

export default function EquipmentViewerPage() {
  const { user, isLoading: authLoading } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery] = useDebouncedValue(searchQuery, 300);
  const [page, setPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>(null);
  const [selectedBrand, setSelectedBrand] = useState<string | null>(null);

  // Merge/unmerge panel state
  const [unmergeTarget, setUnmergeTarget] = useState<{
    productId: string;
    source: 'brs' | 'charterhouse';
  } | null>(null);
  const [mergeSourceId, setMergeSourceId] = useState<string | null>(null);

  const isAdmin = user?.is_fish_admin ?? false;

  const { data: categoriesData } = useEquipmentCatalogCategories();
  const { data: brandsData } = useEquipmentCatalogBrands(selectedCategory || undefined);

  const filters = useMemo(
    () => ({
      q: debouncedQuery || undefined,
      category: selectedCategory || undefined,
      subcategory: selectedSubcategory || undefined,
      brand: selectedBrand || undefined,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    }),
    [debouncedQuery, selectedCategory, selectedSubcategory, selectedBrand, page]
  );

  const { data, isLoading, error, refetch } = useEquipmentViewer(filters);

  const totalPages = data ? Math.ceil(data.total_count / PAGE_SIZE) : 0;

  // Build category options
  const categoryOptions = useMemo(() => {
    if (!categoriesData?.categories) return [];
    return categoriesData.categories.map((cat) => ({
      value: cat.id,
      label: cat.name,
    }));
  }, [categoriesData]);

  // Build subcategory options based on selected category
  const subcategoryOptions = useMemo(() => {
    if (!categoriesData?.categories || !selectedCategory) return [];
    const category = categoriesData.categories.find((c) => c.id === selectedCategory);
    if (!category) return [];
    return category.subcategories.map((sub) => ({
      value: sub.id,
      label: sub.name,
    }));
  }, [categoriesData, selectedCategory]);

  // Build brand options
  const brandOptions = useMemo(() => {
    if (!brandsData?.brands) return [];
    return brandsData.brands.map((brand) => ({
      value: brand.id,
      label: brand.name,
    }));
  }, [brandsData]);

  // Show loading while checking auth
  if (authLoading) {
    return (
      <Container size="xl" py="xl">
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      </Container>
    );
  }

  // Redirect non-admin users
  if (!isAdmin) {
    return (
      <Container size="xl" py="xl">
        <Alert icon={<IconAlertCircle size={16} />} title="Access Denied" color="red">
          You do not have permission to access the Equipment Data Viewer. This page is
          restricted to administrators only.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Title order={1}>Equipment Data Viewer</Title>
        <Text c="dimmed" size="sm">
          Admin tool for reviewing merged equipment catalog data from BRS and Charterhouse.
        </Text>

        {error && (
          <Alert icon={<IconAlertCircle size={16} />} title="Error loading data" color="red">
            {error instanceof Error ? error.message : 'An unexpected error occurred'}
          </Alert>
        )}

        <Paper p="md" withBorder>
          <Stack gap="md">
            {/* Search and filters */}
            <Group>
              <TextInput
                placeholder="Search by name or brand..."
                leftSection={<IconSearch size={16} />}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                style={{ flex: 1, maxWidth: 300 }}
              />
              <Select
                placeholder="Category..."
                data={categoryOptions}
                value={selectedCategory}
                onChange={(value) => {
                  setSelectedCategory(value);
                  setSelectedSubcategory(null);
                  setSelectedBrand(null);
                  setPage(1);
                }}
                clearable
                leftSection={<IconFilter size={16} />}
                style={{ minWidth: 150 }}
              />
              <Select
                placeholder="Subcategory..."
                data={subcategoryOptions}
                value={selectedSubcategory}
                onChange={(value) => {
                  setSelectedSubcategory(value);
                  setPage(1);
                }}
                clearable
                disabled={!selectedCategory}
                style={{ minWidth: 150 }}
              />
              <Select
                placeholder="Brand..."
                data={brandOptions}
                value={selectedBrand}
                onChange={(value) => {
                  setSelectedBrand(value);
                  setPage(1);
                }}
                clearable
                searchable
                style={{ minWidth: 150 }}
              />
            </Group>

            {/* Results count */}
            {data && (
              <Text size="sm" c="dimmed">
                Showing {data.entries.length} of {data.total_count} products
                {data.query && ` matching "${data.query}"`}
              </Text>
            )}

            {/* Data Table */}
            {isLoading ? (
              <Center py="xl">
                <Loader size="lg" />
              </Center>
            ) : (
              <ScrollArea>
                <Table striped highlightOnHover withTableBorder withColumnBorders>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ minWidth: 200 }}>Product</Table.Th>
                      <Table.Th style={{ minWidth: 120 }}>Brand</Table.Th>
                      <Table.Th style={{ minWidth: 120 }}>Category</Table.Th>
                      <Table.Th style={{ minWidth: 120 }}>Sources</Table.Th>
                      <Table.Th style={{ minWidth: 100 }}>Prices</Table.Th>
                      <Table.Th style={{ minWidth: 100 }}>BRS</Table.Th>
                      <Table.Th style={{ minWidth: 100 }}>Charterhouse</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {data?.entries && data.entries.length > 0 ? (
                      data.entries.map((entry: EquipmentViewerEntry) => (
                        <Table.Tr key={entry.id}>
                          <Table.Td>
                            <Text size="sm" fw={500} lineClamp={2}>
                              {entry.name}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{entry.brand_name || '-'}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Stack gap={2}>
                              <Badge size="xs" variant="light">
                                {entry.category_name || '-'}
                              </Badge>
                              <Text size="xs" c="dimmed">
                                {entry.subcategory_name}
                              </Text>
                            </Stack>
                          </Table.Td>
                          <Table.Td>
                            <Group gap="xs">
                              <Badge
                                color={entry.source_count >= 2 ? 'green' : 'gray'}
                                variant="filled"
                              >
                                {entry.source_count}
                              </Badge>
                              {entry.source_count >= 2 && (
                                <>
                                  {entry.url_brs && (
                                    <Tooltip label="Unmerge BRS">
                                      <ActionIcon
                                        size="xs"
                                        variant="subtle"
                                        color="red"
                                        onClick={() => setUnmergeTarget({ productId: entry.id, source: 'brs' })}
                                      >
                                        <IconUnlink size={12} />
                                      </ActionIcon>
                                    </Tooltip>
                                  )}
                                  {entry.url_charterhouse && (
                                    <Tooltip label="Unmerge Charterhouse">
                                      <ActionIcon
                                        size="xs"
                                        variant="subtle"
                                        color="red"
                                        onClick={() => setUnmergeTarget({ productId: entry.id, source: 'charterhouse' })}
                                      >
                                        <IconUnlink size={12} />
                                      </ActionIcon>
                                    </Tooltip>
                                  )}
                                </>
                              )}
                              <Tooltip label="Merge into another product">
                                <ActionIcon
                                  size="xs"
                                  variant="subtle"
                                  color="blue"
                                  onClick={() => setMergeSourceId(entry.id)}
                                >
                                  <IconGitMerge size={12} />
                                </ActionIcon>
                              </Tooltip>
                            </Group>
                          </Table.Td>
                          <Table.Td>
                            <PriceCell usd={entry.price_usd} gbp={entry.price_gbp} />
                          </Table.Td>
                          <Table.Td>
                            <SourceLink url={entry.url_brs} label="BRS" />
                          </Table.Td>
                          <Table.Td>
                            <SourceLink url={entry.url_charterhouse} label="CH" />
                          </Table.Td>
                        </Table.Tr>
                      ))
                    ) : (
                      <Table.Tr>
                        <Table.Td colSpan={7}>
                          <Center py="xl">
                            <Text c="dimmed">
                              {debouncedQuery
                                ? 'No equipment found matching your search'
                                : 'No equipment data available. Run the merge script first.'}
                            </Text>
                          </Center>
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <Center>
                <Pagination total={totalPages} value={page} onChange={setPage} withEdges />
              </Center>
            )}
          </Stack>
        </Paper>
      </Stack>

      {/* Unmerge Panel */}
      <EquipmentUnmergePanel
        opened={unmergeTarget !== null}
        onClose={() => setUnmergeTarget(null)}
        productId={unmergeTarget?.productId ?? ''}
        sourceToUnmerge={unmergeTarget?.source ?? 'brs'}
        onSuccess={() => {
          setUnmergeTarget(null);
          refetch();
        }}
      />

      {/* Merge Panel */}
      <EquipmentMergePanel
        opened={mergeSourceId !== null}
        onClose={() => setMergeSourceId(null)}
        sourceProductId={mergeSourceId ?? ''}
        onSuccess={() => {
          setMergeSourceId(null);
          refetch();
        }}
      />
    </Container>
  );
}
